import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { authorization } from 'Module/Auth/Auth'
import { getCampaignLink } from 'Utils/Link'
import { CityPlanViewWrapper } from 'Components/Campaigns/CityPlanView/CityPlanViewWrapper'
import { FilterButton } from 'Components/Filter/FilterButton'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { PageTitle } from 'Components/PageTitle'
import { SectionTitle } from 'Components/Section/SectionTitle'
import { SectionWrapper } from 'Components/Section/SectionWrapper'
import { ButtonDelete } from 'Components/Buttons/ButtonDelete'
import { ModalConfirmation } from 'Components/Modal/ModalConfirmation'
import { HideWhenBlocked } from 'Containers/UIBlock/HideWhenBlocked'
import { deleteCityPlanById } from 'Reducers/Campaigns/actions'
import { setRostersByCityPlanIdStatusFilter } from 'Reducers/Roster/actions'
import { PATHS } from 'Config/Routing/paths'
import CampaignsIcon from 'Assets/Images/icons/campaigns'
import RostersTable from './../../../Components/RostersTable'
import { weekSortBase } from '../../../Utils/Table'
import MissionsOverview from '../../../Components/MissionsOverview'
import { isMissionReadyToPublish } from '../../../shared/missionReadyToPublish'
import StyledBreadcrumbs from '../../../Components/StyledBreadcrumbs'
import CityPlanStatsHeader from '../../../Components/CityPlanStatsHeader'
import { useCityPlan } from '../CityPlan/CityPlanContext'
import { filterRostersByStatus } from '../../../Utils/CityPlan'

/**
 * A roster is ready to publish when it still holds only draft missions and
 * every one of them has been signed off.
 *
 * @param roster
 * @returns {boolean}
 */
const isRosterReadyToPublish = roster =>
  roster.missions.length > 0 && roster.missions.every(isMissionReadyToPublish)

const CityPlanView = props => {
  const { campaignId, cityPlanId } = props.match.params
  const [state, setState] = useState({ showDeleteModal: false })
  const cityPlanContext = useCityPlan()
  const isLetterbox = cityPlanContext.isLetterbox

  /**
   * Render action links
   *
   * @returns {*}
   */
  const renderActionLinks = () => {
    const { rosters } = props

    if (!authorization.isAdmin()) {
      return null
    }

    // A plan can be deleted while nothing in it has been published.
    const canDelete = isLetterbox
      ? rosters.every(r => r.status !== 'AVAILABLE')
      : rosters.filter(r => r.status !== 'DRAFT').length === 0
    if (canDelete) {
      return [
        <ButtonDelete
          key="button-delete"
          className="button-delete"
          onClickHandler={() => setState({ showDeleteModal: true })}
        />,
        <ModalConfirmation
          key="modal-confirmation"
          modalShow={state.showDeleteModal}
          handleCancel={() => setState({ showDeleteModal: false })}
          handleConfirm={deleteCityPlan}
          title="Delete City Plan"
          withoutText
          labelCancel="Cancel"
          labelConfirm="Confirm"
        />,
      ]
    }

    return null
  }

  const deleteCityPlan = () => {
    const { deleteCityPlanById } = props

    const callbackPath = getCampaignLink(campaignId)

    deleteCityPlanById(cityPlanId, callbackPath)
    setState({ showDeleteModal: false })
  }

  /**
   * Render rosters section
   *
   * @returns {*}
   */
  const renderRosters = () => {
    const { rostersStatusFilter, setRostersByCityPlanIdStatusFilter } = props

    return (
      <SectionWrapper className="section-wrapper rosters">
        <SectionTitle className="section-title">Rosters</SectionTitle>

        <FilterButton
          className={rostersStatusFilter === null ? 'active' : ''}
          onClick={() => setRostersByCityPlanIdStatusFilter(null)}
        >
          All
        </FilterButton>
        <FilterButton
          className={rostersStatusFilter === 'AVAILABLE' ? 'active' : ''}
          onClick={() => setRostersByCityPlanIdStatusFilter('AVAILABLE')}
        >
          Published
        </FilterButton>
        <FilterButton
          className={rostersStatusFilter === 'DRAFT' ? 'active' : ''}
          onClick={() => setRostersByCityPlanIdStatusFilter('DRAFT')}
        >
          Draft
        </FilterButton>
        <FilterButton
          className={rostersStatusFilter === 'READY_TO_PUBLISH' ? 'active' : ''}
          onClick={() => setRostersByCityPlanIdStatusFilter('READY_TO_PUBLISH')}
        >
          Ready to Publish
        </FilterButton>
        {renderRostersTable()}
      </SectionWrapper>
    )
  }

  /**
   * Render rosters table
   *
   * @returns {*}
   */
  const renderRostersTable = () => {
    const rosters = getFilteredRosters()
    return (
      <RostersTable
        rosters={rosters}
        campaignId={campaignId}
        cityPlanId={cityPlanId}
      />
    )
  }

  /**
   * Return filtered list of rosters
   */
  const getFilteredRosters = () => {
    const { rosters } = props
    const { rostersStatusFilter } = props
    // Letterbox plans: rosters carry counts from missionStats, not missions.
    if (isLetterbox) return filterRostersByStatus(rosters, rostersStatusFilter)
    if (rostersStatusFilter === 'AVAILABLE')
      return rosters.filter(r => r.missions.some(m => m.status >= 'AVAILABLE'))
    if (rostersStatusFilter === 'READY_TO_PUBLISH')
      return rosters.filter(isRosterReadyToPublish)
    if (rostersStatusFilter === 'DRAFT')
      return rosters.filter(
        r =>
          r.missions.every(m => m.status == 'DRAFT') &&
          !isRosterReadyToPublish(r),
      )

    return rosters.sort(weekSortBase)
  }

  const { campaign, cityPlan, city } = props
  const pageTitle = `${cityPlan.city ? cityPlan.city.name + ' ' : ''}City Plan`

  return (
    <CityPlanViewWrapper>
      <LayoutDashboard>
        <HideWhenBlocked>
          <StyledBreadcrumbs
            icon={<CampaignsIcon style={{ width: 16 }} />}
            breadcrumbs={[
              {
                label: 'Campaigns',
                link: `${PATHS.main.dashboard}${
                  PATHS.dashboard.campaigns
                }?Status=Active`,
              },
              {
                label: campaign.name,
                link: `${PATHS.main.dashboard}${
                  PATHS.dashboard.campaigns
                }/${campaignId}/city-plans`,
              },
              {
                label: 'City Plan',
              },
            ]}
          />
          <PageTitle>{pageTitle}</PageTitle>
          {isLetterbox ? (
            <CityPlanStatsHeader
              stats={cityPlanContext.stats}
              loading={cityPlanContext.statsLoading}
              error={cityPlanContext.statsError}
              isShowMissionGenerateButton={true}
            />
          ) : (
            <MissionsOverview
              key="cityPlanMissionsOverview"
              rosters={getFilteredRosters()}
              city={city}
              isShowMissionGenerateButton={true}
            />
          )}
          {renderActionLinks()}
          {renderRosters()}
        </HideWhenBlocked>
      </LayoutDashboard>
    </CityPlanViewWrapper>
  )
}

CityPlanView.propTypes = {
  campaign: PropTypes.object.isRequired,
  cityPlan: PropTypes.object.isRequired,
  rosters: PropTypes.array.isRequired,
  rostersStatusFilter: PropTypes.string.isRequired,
  setRostersByCityPlanIdStatusFilter: PropTypes.func.isRequired,
}

export default connect(
  state => ({
    rostersStatusFilter: state.rosters.rostersByCityPlanIdStatusFilter,
  }),
  {
    deleteCityPlanById,
    setRostersByCityPlanIdStatusFilter,
  },
)(CityPlanView)
